import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Books.css";
import { apiUrl } from "../lib/api";
import { withLanguageMetadata } from "../data/bookLanguages";

const normalize = (value) => String(value || "").toLocaleLowerCase().trim();

export default function BooksList() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [englishSearch, setEnglishSearch] = useState("");
  const [arabicSearch, setArabicSearch] = useState("");

  const fetchBooks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(apiUrl("/api/books"));
      if (!response.ok) throw new Error(`Failed to load books: ${response.status}`);
      const data = await response.json();
      setBooks(data.map(withLanguageMetadata));
    } catch (requestError) {
      try {
        const fallbackResponse = await fetch("/books.json");
        if (!fallbackResponse.ok) throw new Error("The book catalogue is unavailable.");
        const fallbackData = await fallbackResponse.json();
        setBooks(fallbackData.map(withLanguageMetadata));
      } catch (fallbackError) {
        setError(`${requestError.message} ${fallbackError.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const filteredBooks = useMemo(() => {
    const englishQuery = normalize(englishSearch);
    const arabicQuery = normalize(arabicSearch);
    return books.filter((book) => {
      const englishText = normalize([
        book.title,
        book.author,
        book.genre,
        book.description,
        book.year,
      ].join(" "));
      const arabicText = normalize([book.arabicTitle, book.arabicDescription].join(" "));
      return (!englishQuery || englishText.includes(englishQuery)) &&
        (!arabicQuery || arabicText.includes(arabicQuery));
    });
  }, [books, englishSearch, arabicSearch]);

  const openEdition = (event, url) => {
    event.stopPropagation();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return <div className="books-container"><h2>Books</h2><div className="books-loading">Loading books…</div></div>;
  }

  if (error) {
    return (
      <div className="books-container">
        <h2>Books</h2>
        <div className="books-error">
          <strong>Unable to load the books.</strong>
          <p>{error}</p>
          <button type="button" onClick={fetchBooks}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="books-container">
      <header className="books-heading">
        <p className="books-heading__eyebrow">The writings of Father Paul Oneid</p>
        <h2>Books</h2>
        <p>Search the collection in English or Arabic and select an available language edition.</p>
      </header>

      <div className="books-search" role="search" aria-label="Search books in English or Arabic">
        <label className="books-search__field">
          <span>Search in English</span>
          <input
            type="search"
            value={englishSearch}
            onChange={(event) => setEnglishSearch(event.target.value)}
            placeholder="Title, subject, year, or author"
          />
        </label>
        <label className="books-search__field books-search__field--arabic" dir="rtl">
          <span>البحث باللغة العربية</span>
          <input
            type="search"
            value={arabicSearch}
            onChange={(event) => setArabicSearch(event.target.value)}
            placeholder="العنوان أو الموضوع أو السنة أو المؤلف"
            lang="ar"
            dir="rtl"
          />
        </label>
      </div>

      <p className="books-results" aria-live="polite">
        Showing {filteredBooks.length} of {books.length} books
      </p>

      <div className="books-grid">
        {filteredBooks.map((book) => (
          <article
            key={book.id}
            className="book-card"
            onClick={() => navigate(`/books/${book.id}`)}
          >
            <div className="book-image">
              {book.image_url || book.cover ? (
                <img
                  src={book.image_url || book.cover}
                  alt={book.title}
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                    event.currentTarget.nextElementSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <div className="book-image-placeholder" style={{ display: book.image_url || book.cover ? "none" : "flex" }}>
                <span className="placeholder-text">{book.title}</span>
              </div>
            </div>

            <div className="book-card-content">
              <h3 className="book-title">{book.title}</h3>
              <p className="book-title-arabic" lang="ar" dir="rtl">{book.arabicTitle}</p>
              <p className="book-author">by {book.author}</p>
              {book.year && <p className="book-year">Published: {book.year}</p>}
              {book.genre && <p className="book-year">Genre: {book.genre}</p>}
              {book.description && <p className="book-description">{book.description}</p>}

              <div className="book-language-actions" aria-label={`Language editions for ${book.title}`}>
                <button
                  type="button"
                  className="book-language-button"
                  onClick={(event) => openEdition(event, book.arabicPdfUrl)}
                >
                  Arabic
                  <span lang="ar" dir="rtl">العربية</span>
                </button>
                <button
                  type="button"
                  className="book-language-button book-language-button--english"
                  onClick={(event) => openEdition(event, book.englishPdfUrl)}
                  disabled={!book.englishPdfUrl}
                  title={!book.englishPdfUrl ? "English translation in preparation" : "Open English edition"}
                >
                  English
                  <span>{book.englishPdfUrl ? "Available" : "In preparation"}</span>
                </button>
              </div>

              <span className="book-link">View Book Details →</span>
            </div>
          </article>
        ))}
      </div>

      {filteredBooks.length === 0 && (
        <div className="books-empty">
          <p>No books match this search.</p>
          <p lang="ar" dir="rtl">لا توجد كتب مطابقة لهذا البحث.</p>
        </div>
      )}
    </div>
  );
}
